import React, { FC, memo, useContext, useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { isEmpty } from "lodash";
import { LinearProgress, Icon } from "@evergis/ui";

import { DashboardContent, DashboardPlaceholder, DashboardPlaceholderWrap, DashboardWrapper } from "../styled";
import { PaginationContainer } from "../../FeatureCard/containers/PaginationContainer";
import { DashboardContext } from "./DashboardContext";
import { ContainersGroupContainer } from "../../FeatureCard/containers/ContainersGroupContainer";
import { renderElement } from "../../FeatureCard/utils/renderElement";
import { getProjectDataSourcesAreLoading } from "../../../ducks/project/selectors";
import { useWidgetContext } from "../hooks/useWidgetContext";
import { useWidgetConfiguration } from "../hooks/useWidgetConfiguration";
import { useConfigPages } from "../hooks/useConfigPages";
import { usePageConfig } from "../hooks/usePageConfig";
import { WidgetType } from "../../FeatureCard/types";

export const DashboardContainer: FC = memo(() => {
  const { t } = useTranslation("featureCard");
  const { expandedContainers, selectedTabId, setSelectedTabId } = useContext(DashboardContext);
  const { dataSources, page } = useWidgetContext(WidgetType.Dashboard);
  const config = useWidgetConfiguration(WidgetType.Dashboard);
  const isLoading = useSelector(getProjectDataSourcesAreLoading);
  const { pages, currentPageIndex } = useConfigPages(WidgetType.Dashboard);
  const { getPageImage } = usePageConfig(WidgetType.Dashboard);
  const prevPage = useRef(page);

  const render = useMemo(
    () =>
      renderElement({
        type: WidgetType.Dashboard,
        config: config.currentConfig,
        expandedContainers,
        selectedTabId,
        setSelectedTabId,
        page,
      }),
    [config, expandedContainers, page, selectedTabId, setSelectedTabId],
  );

  const formatConfig = { ...config, options: { ...config.options, column: false } };
  const isDiffPage = prevPage.current !== page;

  useEffect(() => {
    prevPage.current = page;
  }, [page]);

  if (isEmpty(config.currentConfig)) {
    return (
      <DashboardPlaceholderWrap>
        <DashboardPlaceholder>
          <Icon kind="dashboard_placeholder" />
          <div>{t("dashboardSoon")}</div>
        </DashboardPlaceholder>
      </DashboardPlaceholderWrap>
    );
  }

  return (
    <DashboardWrapper hasImage={!!getPageImage(currentPageIndex)}>
      {!!pages.length && <PaginationContainer config={formatConfig} type={WidgetType.Dashboard} />}
      {(config.currentConfig.dataSources?.length && !dataSources?.length && isLoading) || isDiffPage ? (
        <DashboardPlaceholderWrap>
          <DashboardPlaceholder>
            <Icon kind="dashboard_loading" />
            <div style={{ width: "8rem" }}>
              <LinearProgress />
            </div>
          </DashboardPlaceholder>
        </DashboardPlaceholderWrap>
      ) : (
        <DashboardContent>
          <ContainersGroupContainer config={formatConfig} elementConfig={config.currentConfig} renderElement={render} />
        </DashboardContent>
      )}
    </DashboardWrapper>
  );
});
